import {
  AuditTrailItemDto,
  AuditTrailResponse,
  UserShiftDto,
} from "@bluecrew/finance-service-node-client";
import { isBefore } from "date-fns";
import {
  AuditEditType,
  TransformedAuditData,
  TransformedPair,
  TransformedDuration,
  TransformedPairOrDuration
} from "../types";
import { TimesheetsShiftInfo, UserShift } from "../../../../types/Shift.types";
import { AuditEditFactory } from "./auditEditFactory";

const getLegacyShiftCutoffDate = (): Date => {
  const cutoffDate = import.meta.env.VITE_LEGACY_SHIFT_CUTOFF_DATE;

  return new Date(cutoffDate);
};

const isLegacyShift = (userShift: UserShift): boolean => {
  return isBefore(new Date(userShift.start.time), getLegacyShiftCutoffDate());
};

const sortEditsByDate = <T extends TransformedPairOrDuration>(edits: T[]): T[] => {
  return [...edits].sort((a, b) => {
    if (!a.updateDate && !b.updateDate) return 0;
    if (!a.updateDate) return 1;
    if (!b.updateDate) return -1;
    return b.updateDate.getTime() - a.updateDate.getTime();
  });
};

const keepActualEditsOnly = <T extends TransformedPairOrDuration>(edits: T[]): T[] => {
  const filteredEdits: T[] = [];
  let prevEdit: T | undefined;

  // Iterate backwards since array is newest → oldest
  for (let i = edits.length - 1; i >= 0; i -= 1) {
    if (AuditEditFactory.isActualEdit(edits[i], prevEdit)) {
      filteredEdits.unshift(edits[i]); // Add to beginning to maintain newest → oldest order
      prevEdit = edits[i];
    }
  }
  return filteredEdits;
}

/**
 * Sorts edits by date and ensures CLIENT_SUPPORT appears first (most current)
 */
const prioritizeClientSupport = <T extends TransformedPairOrDuration>(edits: T[]): T[] => {
  const sortedEdits = sortEditsByDate(edits);
  const clientSupportIndex = sortedEdits.findIndex(
    (edit) => edit.editType === AuditEditType.CLIENT_SUPPORT,
  );
  if (clientSupportIndex > 0) {
    const clientSupportEdit = sortedEdits[clientSupportIndex];
    sortedEdits.splice(clientSupportIndex, 1);
    sortedEdits.unshift(clientSupportEdit);
  }
  return keepActualEditsOnly(sortedEdits);
};

const processLegacyBreaks = (
  original: UserShiftDto | undefined,
  edits: AuditTrailItemDto[],
  shiftInfo: TimesheetsShiftInfo,
): TransformedDuration[][] => {
  if (
    !shiftInfo.userShift.break.durationMinutes ||
    shiftInfo.userShift.break.durationMinutes <= 0
  ) {
    return [];
  }

  const breakHistory: TransformedDuration[] = [];

  if (original?.break.durationMinutes) {
    breakHistory.push(
      AuditEditFactory.createLegacyBreak(
        shiftInfo,
        original,
        AuditEditType.ORIGINAL,
        'from-user-shift'
      ),
    );
  }

  edits.forEach((edit) => {
    if (
      edit.userShift?.break.durationMinutes &&
      edit.editTimestamp &&
      edit.editDescription === "Crew member"
    ) {
      breakHistory.push(
        AuditEditFactory.createLegacyBreak(
          shiftInfo,
          edit.userShift,
          edit.editDescription,
          edit.editTimestamp
        ),
      );
    }
  });

  // CLIENT_SUPPORT uses duration from shiftInfo for legacy shifts
  breakHistory.push(
    AuditEditFactory.createLegacyBreak(
      shiftInfo,
      'from-shift-info',
      AuditEditType.CLIENT_SUPPORT,
      'from-user-shift'
    ),
  );

  if (breakHistory.length === 0) {
    return [];
  }

  return [prioritizeClientSupport(breakHistory)];
};

const processNonLegacyBreaks = (
  original: UserShiftDto | undefined,
  edits: AuditTrailItemDto[],
  shiftInfo: TimesheetsShiftInfo | undefined,
): TransformedPair[][] => {
  const maxNumBreaks = Math.max(
    original?.break.breakTimestamps.length || 0,
    ...edits.map((edit) => edit.userShift?.break.breakTimestamps.length || 0),
  );

  const breaks: TransformedPair[][] = [];

  for (let i = 0; i < maxNumBreaks; i += 1) {
    const breakHistory: TransformedPair[] = [];

    if (original?.break.breakTimestamps[i] && shiftInfo) {
      const breakTimestamp = original.break.breakTimestamps[i];
      breakHistory.push(
        AuditEditFactory.createBreakFromTimestamps(
          breakTimestamp,
          original,
          AuditEditType.ORIGINAL,
          'from-user-shift',
          shiftInfo,
        ),
      );
    }

    edits.forEach((edit) => {
      if (edit.userShift && edit.editTimestamp && shiftInfo) {
        const breakTimestamp = edit.userShift.break.breakTimestamps.at(i);
        if (breakTimestamp) {
          breakHistory.push(
            AuditEditFactory.createBreakFromTimestamps(
              breakTimestamp,
              edit.userShift,
              edit.editDescription,
              edit.editTimestamp,
              shiftInfo
            ),
          );
        } else if (breakHistory.length > 0 && !breakHistory[breakHistory.length - 1].deleted) {
          const lastNonDeletedBreak = breakHistory[breakHistory.length - 1];
          breakHistory.push(
            AuditEditFactory.createBreakFromPrevious(
              lastNonDeletedBreak,
              edit.userShift,
              edit.editDescription,
              edit.editTimestamp,
              true
            ),
          );
        }
      }
    });

    if (breakHistory.length > 0) {
      breaks.push(keepActualEditsOnly(sortEditsByDate(breakHistory)));
    }
  }

  return breaks;
};

const transformLegacyShift = (
  { original, edits }: AuditTrailResponse,
  shiftInfo: TimesheetsShiftInfo,
): TransformedAuditData => {
  const clockInOut: TransformedPair[] = [];

  clockInOut.push(AuditEditFactory.createOriginalClockInOut(original, shiftInfo));

  edits.forEach((edit) => {
    if (edit.editDescription === AuditEditType.CREW_MEMBER) {
      const clockEdit = AuditEditFactory.createClockInOutFromEdit(edit);
      if (clockEdit) {
        clockInOut.push(clockEdit);
      }
    }
  });
  clockInOut.push(AuditEditFactory.createClientSupportClockInOut(shiftInfo));

  const breaks = processLegacyBreaks(original, edits, shiftInfo);

  return {
    clockInOut: prioritizeClientSupport(clockInOut),
    breaks,
  };
};

const transformShift = (
  auditTrail: AuditTrailResponse,
  shiftInfo: TimesheetsShiftInfo | undefined,
): TransformedAuditData => {
  const { edits, original } = auditTrail;
  const clockInOut: TransformedPair[] = [];

  if (shiftInfo) {
    clockInOut.push(AuditEditFactory.createOriginalClockInOut(original, shiftInfo));

    edits.forEach((edit) => {
      const clockEdit = AuditEditFactory.createClockInOutFromEdit(edit);
      if (clockEdit) {
        clockInOut.push(clockEdit);
      }
    });
  }

  const breaks = processNonLegacyBreaks(original, edits, shiftInfo);
  return { clockInOut: keepActualEditsOnly(sortEditsByDate(clockInOut)), breaks };
};

/**
 * Transforms audit trail data into UI format.
 * Legacy shifts (before VITE_LEGACY_SHIFT_CUTOFF_DATE) show max 3 items with CLIENT_SUPPORT first.
 */
export const transformAuditResponse = (
  auditTrail: AuditTrailResponse,
  shiftInfo: TimesheetsShiftInfo,
): TransformedAuditData => {
  if (isLegacyShift(shiftInfo.userShift)) {
    return transformLegacyShift(auditTrail, shiftInfo);
  }

  return transformShift(auditTrail, shiftInfo);
};
